import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SupabaseService, DocumentItem } from './supabase.service';
import { MediaValidatorService } from './media-validator.service';

export interface LocalUploadTask {
  id: string;
  fileName: string;
  fileType: 'image' | 'video' | 'audio' | 'document';
  bucket: 'images' | 'videos' | 'audio' | 'documents';
  fileBlob: Blob;
  sizeBytes: number;
  progress: number; // 0 to 100
  status: 'pending' | 'uploading' | 'completed' | 'failed';
  error?: string;
  publicUrl?: string;
  createdAt: number;
}

const DB_NAME = 'med_elearning_uploads_db';
const STORE_NAME = 'upload_queue';
const DB_VERSION = 1;

@Injectable({ providedIn: 'root' })
export class UploadManagerService {
  private dbPromise: Promise<IDBDatabase> | null = null;
  private tasksSubject = new BehaviorSubject<LocalUploadTask[]>([]);
  public tasks$: Observable<LocalUploadTask[]> = this.tasksSubject.asObservable();

  private isProcessing = false;

  constructor(
    private readonly supabaseService: SupabaseService,
    private readonly mediaValidator: MediaValidatorService
  ) {
    this.initDB().then(() => {
      this.loadSavedTasks();
    });
  }

  private initDB(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise((resolve, reject) => {
      if (typeof window === 'undefined' || !window.indexedDB) {
        reject(new Error('IndexedDB is not available in this environment.'));
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    return this.dbPromise;
  }

  private async getStore(mode: IDBTransactionMode): Promise<IDBObjectStore> {
    const db = await this.initDB();
    const tx = db.transaction(STORE_NAME, mode);
    return tx.objectStore(STORE_NAME);
  }

  private async loadSavedTasks(): Promise<void> {
    try {
      const store = await this.getStore('readonly');
      const request = store.getAll();
      request.onsuccess = () => {
        const tasks: LocalUploadTask[] = request.result || [];
        // Sort by creation time
        tasks.sort((a, b) => b.createdAt - a.createdAt);
        this.tasksSubject.next(tasks);
        
        // Auto resume any pending/interrupted uploading tasks
        const hasUnfinished = tasks.some(t => t.status === 'pending' || t.status === 'uploading');
        if (hasUnfinished) {
          // Reset interrupted uploading to pending
          tasks.forEach(t => {
            if (t.status === 'uploading') t.status = 'pending';
          });
          this.saveTasksToDB(tasks);
          this.processQueue();
        }
      };
    } catch (e) {
      console.warn('[UploadManager] Failed to load saved tasks from IndexedDB:', e);
    }
  }

  private async saveTaskToDB(task: LocalUploadTask): Promise<void> {
    try {
      const store = await this.getStore('readwrite');
      store.put(task);
    } catch (e) {
      console.warn('[UploadManager] Failed to save task to IndexedDB:', e);
    }
  }

  private async removeTaskFromDB(id: string): Promise<void> {
    try {
      const store = await this.getStore('readwrite');
      store.delete(id);
    } catch (e) {
      console.warn('[UploadManager] Failed to delete task from IndexedDB:', e);
    }
  }

  private async saveTasksToDB(tasks: LocalUploadTask[]): Promise<void> {
    for (const t of tasks) {
      await this.saveTaskToDB(t);
    }
  }

  /**
   * Enqueue a new file upload to be processed in background and persisted in IndexedDB.
   */
  public async enqueueUpload(
    file: File | Blob,
    fileName: string,
    fileType: LocalUploadTask['fileType'],
    bucket: LocalUploadTask['bucket']
  ): Promise<LocalUploadTask> {
    // Validate video duration limit (max 5 minutes / 300 seconds)
    if (fileType === 'video' || file.type.startsWith('video/')) {
      await this.mediaValidator.validateVideoDuration(file, 300);
    }

    const task: LocalUploadTask = {
      id: crypto.randomUUID(),
      fileName: fileName || (file as File).name || `file_${Date.now()}`,
      fileType,
      bucket,
      fileBlob: file,
      sizeBytes: file.size,
      progress: 0,
      status: 'pending',
      createdAt: Date.now(),
    };

    await this.saveTaskToDB(task);
    const updatedTasks = [task, ...this.tasksSubject.value];
    this.tasksSubject.next(updatedTasks);

    // Trigger processing queue
    this.processQueue();
    return task;
  }

  /**
   * Process pending uploads in the background queue.
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      while (true) {
        const tasks = this.tasksSubject.value;
        const nextTask = tasks.find((t) => t.status === 'pending');

        if (!nextTask) break;

        await this.executeUpload(nextTask);
      }
    } finally {
      this.isProcessing = false;
    }
  }

  private async executeUpload(task: LocalUploadTask): Promise<void> {
    this.updateTaskState(task.id, { status: 'uploading', progress: 10, error: undefined });

    try {
      const user = await this.supabaseService.getCurrentUser();
      if (!user) throw new Error('No user logged in.');

      const fileExt = task.fileName.split('.').pop() || 'bin';
      const storagePath = `${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${fileExt}`;

      // Simulate progress stepping for smooth UX during upload
      this.updateTaskState(task.id, { progress: 35 });

      const fileToUpload = new File([task.fileBlob], task.fileName, { type: task.fileBlob.type });

      const { error: uploadError } = await this.supabaseService.client.storage
        .from(task.bucket)
        .upload(storagePath, fileToUpload, { upsert: true });

      if (uploadError) throw uploadError;

      this.updateTaskState(task.id, { progress: 80 });

      const { data: publicUrlData } = this.supabaseService.client.storage
        .from(task.bucket)
        .getPublicUrl(storagePath);

      const publicUrl = publicUrlData.publicUrl;

      // Register in documents database table if not present
      const { data, error: dbError } = await this.supabaseService.client
        .from('documents')
        .insert([
          {
            title: task.fileName,
            file_url: publicUrl,
            file_type: task.fileType,
            size_bytes: task.sizeBytes,
            bucket_id: task.bucket,
            uploaded_by: user.id,
          },
        ])
        .select()
        .single();

      if (dbError) throw dbError;

      this.updateTaskState(task.id, {
        status: 'completed',
        progress: 100,
        publicUrl: publicUrl,
      });

    } catch (err: any) {
      console.error(`[UploadManager] Upload failed for task ${task.id}:`, err);
      this.updateTaskState(task.id, {
        status: 'failed',
        error: err?.message || 'Upload failed due to connection error.',
      });
    }
  }

  private updateTaskState(id: string, patch: Partial<LocalUploadTask>): void {
    const currentTasks = this.tasksSubject.value;
    const taskIndex = currentTasks.findIndex((t) => t.id === id);
    if (taskIndex === -1) return;

    const updatedTask = { ...currentTasks[taskIndex], ...patch };
    const newTasks = [...currentTasks];
    newTasks[taskIndex] = updatedTask;

    this.tasksSubject.next(newTasks);
    this.saveTaskToDB(updatedTask);
  }

  public async retryUpload(id: string): Promise<void> {
    const task = this.tasksSubject.value.find((t) => t.id === id);
    if (!task) return;

    this.updateTaskState(id, { status: 'pending', error: undefined, progress: 0 });
    this.processQueue();
  }

  public async removeTask(id: string): Promise<void> {
    await this.removeTaskFromDB(id);
    const updatedTasks = this.tasksSubject.value.filter((t) => t.id !== id);
    this.tasksSubject.next(updatedTasks);
  }

  public async clearCompletedTasks(): Promise<void> {
    const completed = this.tasksSubject.value.filter((t) => t.status === 'completed');
    for (const task of completed) {
      await this.removeTaskFromDB(task.id);
    }
    const remaining = this.tasksSubject.value.filter((t) => t.status !== 'completed');
    this.tasksSubject.next(remaining);
  }
}
